import React, { FC, useEffect, useMemo } from 'react';
import cs from 'classnames';
import { useTranslation } from '@wix/wix-i18n-config';
import { useParams } from 'react-router';

import { TemplatesGrid } from '../TemplatesGrid/TemplatesGrid';
import { InspirationalTemplatesPage } from './InspirationalTemplatesPage/InspirationalTemplatesPage';
import { useInitialState } from '../../contexts/InitialStateContext';
import { useBiLogger } from '../../contexts/BILoggerContext';
import { templatesPageDataHooks } from './TemplatesPage.dataHooks';
import { useSelectedCategory } from '../../hooks/useSelectedCategory';
import {
  DEFAULT_STUDIO_CATEGORY_SLUG,
  DESIGNED_TEMPLATES_CATEGORY_SLUG,
  INSPIRATIONAL_CATEGORY_SLUG,
} from '../../config';
import { useExperiments } from '../../contexts/ExperimentsContext';

import s from './TemplatesPage.scss';
import { useSearch } from '../../hooks/useSearch';

interface TemplatesPageProps {}

const TemplatesPage: FC<TemplatesPageProps> = () => {
  const initialState = useInitialState();
  const biLogger = useBiLogger();
  const { t } = useTranslation();
  const experiments = useExperiments();
  const { isSearchActive, criteria } = useSearch();
  const { categorySlug = DEFAULT_STUDIO_CATEGORY_SLUG } = useParams();
  const isInspirationalTemplates = categorySlug === INSPIRATIONAL_CATEGORY_SLUG;
  const isAllTemplates = categorySlug === DEFAULT_STUDIO_CATEGORY_SLUG;
  const hasBlankCanvasButton =
    isAllTemplates &&
    experiments.is('specs.funnel.StudioTemplatesTryBlankCanvasButton', 'true');

  useEffect(() => {
    biLogger.reportTemplatesPageView();
  }, [biLogger, categorySlug]);

  const categories = useMemo(
    () => initialState.book?.categories ?? [],
    [initialState.book],
  );

  const selectedCategory = useSelectedCategory({ categories });
  let heading = t('app.new.title.category', {
    category: selectedCategory!.displayName,
  });
  const isHomePage = selectedCategory!.slug === DEFAULT_STUDIO_CATEGORY_SLUG;
  if (isHomePage) {
    heading = t('app.new.title.home');
  } else if (selectedCategory!.slug === DESIGNED_TEMPLATES_CATEGORY_SLUG) {
    heading = t('app.new.title.designed');
  }

  if (isSearchActive) {
    heading = t('app.new.title.search', { criteria });
  }

  return (
    <main
      data-hook={templatesPageDataHooks.container()}
      className={cs(s.main, { [s.withBlankCanvas]: hasBlankCanvasButton })}
    >
      {isInspirationalTemplates ? (
        <InspirationalTemplatesPage heading={selectedCategory!.displayName} />
      ) : (
        <TemplatesGrid
          heading={heading}
          showBlankCanvas={hasBlankCanvasButton}
        />
      )}
    </main>
  );
};

export default TemplatesPage;
